import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';

import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import _ from 'lodash';
import { TabBrickSettingsState } from '../../../brick-settings/specific-brick-settings/tab-brick-settings/tab-brick-settings.state';
import { SetAndPersistSelectedTabIndex } from '../../brick-tab-state/brick-tab.actions';
import { SetTabById, SetTabByIndex, SetTabIndex } from './tab-brick-behaviour.actions';

const DEFAULT_TAB_INDEX = 0;

export interface TabBrickBehaviourInstance {
   selectedTabIndex: number;
}

export interface TabBrickBehaviourStateModel {
   [brickInstanceId: string]: TabBrickBehaviourInstance;
}

function getDefaultTabBrickBehaviourSettings(): TabBrickBehaviourInstance {
   return {
      selectedTabIndex: DEFAULT_TAB_INDEX,
   };
}

@State<TabBrickBehaviourStateModel>({
   name: 'tabBrickBehaviour',
   defaults: {},
})
@Injectable()
export class TabBrickBehaviourState {
   constructor(private store: Store) {}

   private static getSpecificBehaviourStateOrDefaultInternal(state: TabBrickBehaviourStateModel, brickInstanceId: string): TabBrickBehaviourInstance {
      const value = state[brickInstanceId];
      if (value) {
         return value;
      } else {
         return getDefaultTabBrickBehaviourSettings();
      }
   }

   @Selector([TabBrickBehaviourState.getBrickInstance])
   public static getSelectedTabIndex(getBrickInstance: (brickInstanceId: string) => TabBrickBehaviourInstance): (brickInstanceId: string) => number {
      return (brickInstanceId: string) => {
         return getBrickInstance(brickInstanceId)?.selectedTabIndex ?? DEFAULT_TAB_INDEX;
      };
   }

   @Selector([TabBrickBehaviourState])
   private static getBrickInstance(state: TabBrickBehaviourStateModel) {
      return (brickInstanceId: string) => {
         return TabBrickBehaviourState.getSpecificBehaviourStateOrDefaultInternal(state, brickInstanceId);
      };
   }

   @Action(SetTabById)
   @ImmutableContext()
   public setTabById(ctx: StateContext<TabBrickBehaviourStateModel>, action: SetTabById) {
      const index = this.store.selectSnapshot(TabBrickSettingsState.getTabIndex)(action.brickInstanceId, action.tabId);
      if (index < 0) {
         return;
      }

      ctx.dispatch(new SetAndPersistSelectedTabIndex(action.brickInstanceId, index));
   }

   @Action(SetTabByIndex)
   @ImmutableContext()
   public setTabByIndex(ctx: StateContext<TabBrickBehaviourStateModel>, action: SetTabByIndex) {
      const index = action.index;
      if (index < 0) {
         return;
      }

      ctx.dispatch(new SetAndPersistSelectedTabIndex(action.brickInstanceId, index));
   }

   @Action(SetTabIndex)
   @ImmutableContext()
   public setTabIndex(ctx: StateContext<TabBrickBehaviourStateModel>, action: SetTabIndex) {
      const instance: TabBrickBehaviourInstance = _.cloneDeep(this.store.selectSnapshot(TabBrickBehaviourState.getBrickInstance)(action.brickInstanceId));
      ctx.setState((state: TabBrickBehaviourStateModel) => {
         instance.selectedTabIndex = action.index;
         state[action.brickInstanceId] = instance;
         return state;
      });
   }
}
